import { z } from 'zod'

export const WatchMapProvince = z.enum([
  'beijing',
  'tianjin',
  'hebei',
  'shanxi',
  'neimenggu',
  'liaoning',
  'jilin',
  'heilongjiang',
  'shanghai',
  'jiangsu',
  'zhejiang',
  'anhui',
  'fujian',
  'jiangxi',
  'shandong',
  'henan',
  'hubei',
  'hunan',
  'guangdong',
  'guangxi',
  'hainan',
  'chongqing',
  'sichuan',
  'guizhou',
  'yunnan',
  'xizang',
  'shaanxi',
  'gansu',
  'qinghai',
  'ningxia',
  'xinjiang',
  'taiwan',
  'hongkong',
  'macau',
])
export const WatchMapSha256 = z.string().regex(/^[0-9a-f]{64}$/)
export const WatchMapPartId = z.string().regex(/^[0-9]{4}$/)
export const WatchMap = z
  .object({
    province_code: WatchMapProvince,
    source_date: z.string().datetime(),
    byte_count: z
      .number()
      .int()
      .positive()
      .max(256 * (1_073_741_824 - 1)),
    sha256: WatchMapSha256,
    format_version: z.number().int().min(2).max(5).optional(),
    download_path: z.string().regex(/^\/api\/watch-maps\/[a-z]+\/[0-9a-f]{64}$/),
  })
  .superRefine((entry, context) => {
    if (
      entry.download_path !== `/api/watch-maps/${entry.province_code}/${entry.sha256}` ||
      (entry.format_version !== 5 && entry.byte_count >= 1_073_741_824)
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid Watch map release identity or size',
      })
  })
export type WatchMap = z.infer<typeof WatchMap>
export const WatchMapListResponse = z
  .object({ maps: z.array(WatchMap).max(34) })
  .superRefine((catalog, context) => {
    if (new Set(catalog.maps.map((entry) => entry.province_code)).size !== catalog.maps.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Duplicate province release' })
    }
  })

export const WatchMapPart = z
  .object({
    part_id: WatchMapPartId,
    sha256: WatchMapSha256,
    byte_count: z.number().int().positive().max(1_073_741_823),
    tile_id_start: z
      .number()
      .int()
      .min((4 ** 6 - 1) / 3),
    tile_id_end: z
      .number()
      .int()
      .max((4 ** 15 - 1) / 3 - 1),
    tile_count: z.number().int().positive(),
  })
  .strict()

export const WatchMapReleaseDescriptor = z
  .object({
    version: z.literal(1),
    format_version: z.literal(5),
    province_code: WatchMapProvince,
    source_date: z.string().datetime(),
    bounds: z.tuple([
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
      z.number().finite(),
    ]),
    min_zoom: z.literal(6),
    max_zoom: z.literal(14),
    byte_count: z.number().int().positive(),
    attribution: z.string().min(1).max(4096),
    license: z.literal('https://opendatacommons.org/licenses/odbl/1-0/'),
    source: z
      .object({
        identifier: z.literal('osm-copernicus-glo30-2021'),
        demRelease: z.literal('2021'),
        elevationModel: z.literal('DSM'),
        verticalReference: z.literal('EGM2008'),
        contourIntervalMeters: z.literal(20),
        nativeSpacingArcseconds: z.literal(1),
        osmSHA256: WatchMapSha256,
        demReceiptsSHA256: WatchMapSha256,
        demLicense: z.string().url().max(2048),
      })
      .strict(),
    missing_elevation_cells: z
      .array(z.tuple([z.number().int().min(-180).max(179), z.number().int().min(-84).max(84)]))
      .max(4096),
    parts: z.array(WatchMapPart).min(1).max(256),
  })
  .strict()
  .superRefine((release, context) => {
    const [west, south, east, north] = release.bounds
    let next = (4 ** 6 - 1) / 3
    let bytes = 0
    let valid =
      -180 <= west && west < east && east <= 180 && -85 < south && south < north && north < 85
    for (const [index, part] of release.parts.entries()) {
      valid &&=
        part.part_id === String(index).padStart(4, '0') &&
        part.tile_id_start === next &&
        part.tile_id_end >= part.tile_id_start &&
        part.tile_count <= part.tile_id_end - part.tile_id_start + 1
      next = part.tile_id_end + 1
      bytes += part.byte_count
    }
    valid &&=
      next === (4 ** 15 - 1) / 3 &&
      bytes === release.byte_count &&
      new Set(release.missing_elevation_cells.map((cell) => cell.join(','))).size ===
        release.missing_elevation_cells.length
    if (!valid)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Incomplete or inconsistent province release',
      })
  })
export type WatchMapReleaseDescriptor = z.infer<typeof WatchMapReleaseDescriptor>
